import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useUser, User } from '../../authentication';
import WhiteLabel from '../../../filters/WhiteLabel';
import { Course, CourseStep } from '../models/course';
import useManageTrainingRoute from '../../../hooks/useManageTrainingRoute';
import alertService from '../../../services/AlertService';
import trainingService from '../../../services/trainingService';
import windowService from '../../../services/windowService';
import { handleError } from '../../../utils/apiUtils';
import { reorderSortableItems } from '../../../utils/reorderSortableItems';
import { UrlParams } from '../models/url';

const isCourseOwner = (user: User | undefined, course: Course, isAuthoring: boolean) => {
  if (isAuthoring) {
    if (!user?.catalogs?.find(catalog => catalog.shortCode === WhiteLabel.catalog?.shortCode)) {
      return false;
    }
    return !!user?.catalogs?.find(catalog => catalog._id.id === course.owner);
  }

  return user?.lastSelectedAccount === course?.owner;
};

type State = {
  course: Course;
  steps: CourseStep[];
  isOwner: boolean;
  isLoading: boolean;
  isError: boolean;
};

export const useCourseBuilder = () => {
  const { user } = useUser();
  const [data, setData] = useState<State>({
    course: {
      id: '',
      name: '',
      category: '',
      published: false,
      owner: '',
      tasks: [],
      lastModifiedDate: new Date(),
    },
    steps: [],
    isOwner: false,
    isLoading: true,
    isError: false,
  });

  const { buildType, trainingId } = useParams<UrlParams>();
  const { manageTrainingRoutePrefix } = useManageTrainingRoute();
  const hasSteps = data.steps.length > 0;

  const sortSteps = (steps: CourseStep[]) => {
    return steps.sort((a, b) => a.order - b.order);
  };

  const updateSortOrder = (steps: CourseStep[]) => {
    return steps.map((s, index) => ({ ...s, order: index }));
  };

  const loadCourseTasklist = () =>
    trainingService
      .getTasklist(trainingId)
      .then(response => {
        setData(previousData => ({
          ...previousData,
          course: response,
          steps: sortSteps(response.tasks),
          isOwner: isCourseOwner(user, response, buildType === 'author'),
          isLoading: false,
        }));
      })
      .catch(error => {
        handleError(error);
      });

  const reorder = reorderSortableItems(data.steps, (newTasks, originalTasks) => {
    setData(prev => ({ ...prev, steps: updateSortOrder(newTasks) }));

    const orderMap = Object.fromEntries(newTasks.map((s, i) => [s._id.id, i]));

    return trainingService
      .reorderTasks(trainingId, orderMap, user?.userId)
      .then(() => {
        alertService.show('Steps Updated');
      })
      .catch(error => {
        handleError(error);
        setData(prev => ({ ...prev, steps: originalTasks }));
      });
  });

  const addStepToState = (step: CourseStep) => {
    setData(prev => ({
      ...prev,
      steps: [...prev.steps, step],
      course: {
        ...prev.course,
        tasks: [...(prev.course.tasks || []), step],
      },
    }));
    alertService.show('Step Added');
  };

  const editStep = (step: CourseStep) => {
    if (data.steps.find(s => s._id.id === step._id.id)) {
      setData(prev => ({ ...prev, steps: prev.steps.map(s => (s._id.id === step._id.id ? step : s)) }));
    } else {
      // Remove the item currently in the order slot and replace it with the new step.
      setData(prev => {
        const sortedSteps = [...prev.steps.filter(s => s.order !== step.order), step].sort((a, b) => a.order - b.order);
        return {
          ...prev,
          steps: updateSortOrder(sortedSteps),
        };
      });
    }

    alertService.show('Step Edited');
  };

  useEffect(() => {
    loadCourseTasklist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeStep = (stepId: string) =>
    trainingService
      .removeTask(trainingId, stepId, user?.userId)
      .then(() => {
        alertService.show('Step Removed');
        const updatedSteps = data.steps.filter(task => task._id.id !== stepId);

        setData(previousData => ({
          ...previousData,
          steps: updatedSteps,
        }));
      })
      .catch(handleError);

  const publishCourse = () =>
    trainingService.publishCourse(trainingId, user?.userId).then(() => updatePublishedStatus(true));

  const unpublishCourse = () =>
    trainingService.unpublishCourse(trainingId, user?.userId).then(() => updatePublishedStatus(true));

  const handleCourseCopy = (name: string, ownerId: string) =>
    trainingService
      .cloneTraining('course', trainingId, name, ownerId, user?.userId)
      .then(response => {
        // This timeout gives time for the service to fully complete all the changes
        // before redirecting. In some cases, the name wasn't updated in time.
        return new Promise<void>(resolve =>
          setTimeout(() => {
            windowService.redirectTo(`${manageTrainingRoutePrefix}/courses/${response.id}`);
            resolve();
          }, 500)
        );
      })
      .catch(handleError);

  const updatePublishedStatus = (isPublished: boolean) => {
    setData(prev => ({ ...prev, course: { ...prev.course, published: isPublished } }));
  };

  return {
    ...data,
    reorder,
    removeStep,
    publishCourse,
    unpublishCourse,
    handleCourseCopy,
    hasSteps,
    addStepToState,
    editStep,
    reloadCourse: loadCourseTasklist,
  };
};
